import { Link } from "@chakra-ui/next-js";
import { Center, MenuItem, Stack } from "@chakra-ui/react";
import { CollapsiblePanel, OverflowMenu } from "@components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { MoreNavItemsButton } from "@src/components/widgets/Layout/AppNav/MoreNavItemsButton";
import type { TPages } from "@src/config/routes";
import { theme } from "@src/theme";
import type { IPerms } from "@src/utils/components/permissions";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { useStore } from "@src/utils/hooks";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import type { CSSUnitValue } from "@src/utils/types";
import type { IconNames } from "@src/utils/types/iconNames";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import type { Route } from "nextjs-routes";
import { NavItem, type NavItemProps } from "./NavItem";

export type NavigationPage = {
  page: TPages;
  label: string;
  activeOn?: string[];
  icon: IconNames;
  permissions?: IPerms | IPerms[];
} & (
  | {
      url: Route["pathname"];
      subPages?: undefined;
    }
  | {
      url?: undefined;
      subPages: NavigationSubPage[];
    }
);

type NavigationSubPage = {
  label: string;
  url: Route["pathname"];
  permissions?: IPerms | IPerms[];
};

const APP_NAV_WIDTH: CSSUnitValue = theme.space[20];

const MainNav = () => {
  const { authStore, workspaceStore } = useStore();
  const router = useRouter();
  const { isDesktop } = useScreenType();

  return (
    <Stack
      pos="fixed"
      zIndex={100}
      top={{ base: "unset", lg: 0 }}
      bottom={{ base: "0", lg: "unset" }}
      left="0"
      direction={{ base: "row", lg: "column" }}
      overflowX="hidden"
      overflowY="auto"
      w={{ base: "100vw", lg: APP_NAV_WIDTH }}
      h={{ base: 20, lg: "full" }}
      bg="riptide.300"
      style={{ scrollbarWidth: "none" }}
    >
      <Center display={{ base: "none", lg: "flex" }} h="12">
        <Link href="/">
          <Icon name="allfred" h="48px" w="48px" color="white" />
        </Link>
      </Center>
      {authStore.user && (
        <WrapComponent
          if={!isDesktop}
          with={(children) => (
            <CollapsiblePanel
              dropdownMenu={
                <OverflowMenu<NavItemProps> menuButton={MoreNavItemsButton}>
                  {({ collapsedItems, isOpen }) =>
                    collapsedItems.map(({ props: item, key }) => {
                      const url = item.url ?? item.subPages?.[0].url;
                      const isActive = [url, ...(item.activeOn ?? [])].some(
                        (path) => {
                          return router.pathname.includes(path);
                        },
                      );

                      return (
                        <MenuItem
                          key={key}
                          as={Link}
                          bg={isActive ? "violet.100" : undefined}
                          pointerEvents={isOpen ? undefined : "none"}
                          href={url}
                          icon={<Icon name={item.icon} color="grey.900" />}
                        >
                          {item.label}
                        </MenuItem>
                      );
                    })
                  }
                </OverflowMenu>
              }
            >
              {children}
            </CollapsiblePanel>
          )}
        >
          {workspaceStore.VISIBLE_NAVIGATION.map((item) => (
            <NavItem targetId={item.page} key={item.page} {...item} />
          ))}
        </WrapComponent>
      )}
    </Stack>
  );
};

export default observer(MainNav);
